/**
 * PWA Install Prompt Component
 * Shows an "Install FinList" banner when the app is installable
 * 
 * - Android/Desktop: Uses beforeinstallprompt event
 * - iOS Safari: Shows instructions to use Share → Add to Home Screen
 * 
 * Respects user dismissal and doesn't show again for 7 days
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download, Share2, Plus, Check } from 'lucide-react';

// Storage key for dismissal tracking
const DISMISS_KEY = 'finlist-pwa-install-dismissed';
const DISMISS_DURATION_DAYS = 7;

/**
 * Detect if running on iOS
 */
const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

/**
 * Detect if already installed as PWA (standalone mode)
 */
const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

/**
 * Check if prompt was recently dismissed
 */
const wasRecentlyDismissed = () => {
  try {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (!dismissedAt) return false;
    
    const dismissDate = new Date(parseInt(dismissedAt, 10));
    const now = new Date();
    const daysDiff = (now - dismissDate) / (1000 * 60 * 60 * 24);
    
    return daysDiff < DISMISS_DURATION_DAYS;
  } catch {
    return false;
  }
};

/**
 * PWAInstallPrompt Component
 */
export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [installing, setInstalling] = useState(false);
  const containerRef = useRef(null);
  const iosDevice = isIOS();

  // Listen for beforeinstallprompt (Android/Desktop Chrome)
  useEffect(() => {
    // Don't show if already installed or recently dismissed
    if (isStandalone() || wasRecentlyDismissed()) {
      console.log('[PWA] Skipping prompt - standalone:', isStandalone(), 'dismissed:', wasRecentlyDismissed());
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] beforeinstallprompt event received');
      // Prevent Chrome's default mini-infobar
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      // Show our custom prompt
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS Safari, show instructions after a delay if not installed
    if (iosDevice && !isStandalone()) {
      console.log('[PWA] iOS detected, will show instructions');
      const timer = setTimeout(() => {
        if (!wasRecentlyDismissed()) {
          console.log('[PWA] Showing iOS install instructions');
          setShowIOSInstructions(true);
          setShowPrompt(true);
        }
      }, 2000); // Show after 2 seconds on iOS
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [iosDevice]);

  // Handle install button click (Android/Desktop)
  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) {
      console.log('[PWA] No deferred prompt available');
      return;
    }

    setInstalling(true);
    
    try {
      // Show the native install prompt
      deferredPrompt.prompt();
      
      // Wait for user response
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log('[PWA Install] User choice:', outcome);
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
      
      if (outcome === 'dismissed') {
        // User dismissed, remember for 7 days
        localStorage.setItem(DISMISS_KEY, Date.now().toString());
      }
    } catch (error) {
      console.error('[PWA Install] Error:', error);
    } finally {
      setInstalling(false);
    }
  }, [deferredPrompt]);

  // Handle dismiss - using native DOM events for iOS reliability
  const handleDismiss = useCallback(() => {
    console.log('[PWA] User dismissed prompt');
    setShowPrompt(false);
    setDeferredPrompt(null);
    setShowIOSInstructions(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  }, []);

  // Handle "Got it" for iOS
  const handleIOSGotIt = useCallback(() => {
    console.log('[PWA] iOS user acknowledged instructions');
    setShowPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  }, []);

  // Attach native touch handlers for iOS reliability
  useEffect(() => {
    if (!showPrompt || !containerRef.current) return;
    
    const container = containerRef.current;
    const dismissBtn = container.querySelector('[data-dismiss-btn]');
    const gotItBtn = container.querySelector('[data-gotit-btn]');
    const installBtn = container.querySelector('[data-install-btn]');
    
    const handleTouch = (callback) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      callback();
    };
    
    if (dismissBtn) {
      dismissBtn.addEventListener('touchstart', handleTouch(handleDismiss), { passive: false });
    }
    if (gotItBtn) {
      gotItBtn.addEventListener('touchstart', handleTouch(handleIOSGotIt), { passive: false });
    }
    if (installBtn) {
      installBtn.addEventListener('touchstart', handleTouch(handleInstall), { passive: false });
    }
    
    return () => {
      if (dismissBtn) {
        dismissBtn.removeEventListener('touchstart', handleTouch(handleDismiss));
      }
      if (gotItBtn) {
        gotItBtn.removeEventListener('touchstart', handleTouch(handleIOSGotIt));
      }
      if (installBtn) {
        installBtn.removeEventListener('touchstart', handleTouch(handleInstall));
      }
    };
  }, [showPrompt, handleDismiss, handleIOSGotIt, handleInstall]);

  if (!showPrompt) return null;

  // Use inline styles for iOS - Tailwind can be unreliable on iOS Safari
  const containerStyle = {
    position: 'fixed',
    // iOS: show at TOP to avoid Safari toolbar conflicts
    // Others: show at bottom
    ...(iosDevice ? { top: '16px' } : { bottom: '96px' }),
    left: '16px',
    right: '16px',
    zIndex: 2147483647, // Maximum z-index
    WebkitTransform: 'translateZ(0)', // Force GPU layer on iOS
    transform: 'translateZ(0)',
    pointerEvents: 'auto',
    touchAction: 'manipulation',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    userSelect: 'none',
  };

  const cardStyle = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    pointerEvents: 'auto',
  };

  const headerStyle = {
    background: 'linear-gradient(to right, #9333ea, #4f46e5)',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const buttonStyle = {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(to right, #9333ea, #4f46e5)',
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '16px',
    borderRadius: '12px',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    WebkitAppearance: 'none',
    appearance: 'none',
    touchAction: 'manipulation',
    minHeight: '54px',
  };

  const closeButtonStyle = {
    padding: '12px',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '48px',
    minHeight: '48px',
    WebkitAppearance: 'none',
    appearance: 'none',
    touchAction: 'manipulation',
  };

  return (
    <div ref={containerRef} style={containerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ffffff' }}>
            <Download style={{ width: '20px', height: '20px' }} />
            <span style={{ fontWeight: '600' }}>Install FinList</span>
          </div>
          <button
            type="button"
            data-dismiss-btn
            onClick={handleDismiss}
            style={closeButtonStyle}
            aria-label="Dismiss"
          >
            <X style={{ width: '20px', height: '20px', color: '#ffffff' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {showIOSInstructions ? (
            // iOS Instructions
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ fontSize: '14px', color: '#4b5563', margin: 0 }}>
                To install FinList on your iPhone:
              </p>
              
              {/* Step 1 */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '12px', 
                backgroundColor: '#eff6ff', 
                borderRadius: '12px', 
                padding: '12px',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  backgroundColor: '#3b82f6', 
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Share2 style={{ width: '20px', height: '20px' }} />
                </div>
                <div>
                  <p style={{ fontWeight: '600', color: '#111827', margin: 0, fontSize: '14px' }}>
                    Step 1: Tap Share
                  </p>
                  <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: '13px' }}>
                    Find the share icon (square with arrow) at the bottom of Safari
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '12px', 
                backgroundColor: '#f0fdf4', 
                borderRadius: '12px', 
                padding: '12px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  backgroundColor: '#22c55e', 
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Plus style={{ width: '20px', height: '20px' }} />
                </div>
                <div>
                  <p style={{ fontWeight: '600', color: '#111827', margin: 0, fontSize: '14px' }}>
                    Step 2: Add to Home Screen
                  </p>
                  <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: '13px' }}>
                    Scroll down in the share menu and tap "Add to Home Screen"
                  </p>
                </div>
              </div>

              {/* Got it button */}
              <button
                type="button"
                data-gotit-btn
                onClick={handleIOSGotIt}
                style={buttonStyle}
              >
                <Check style={{ width: '20px', height: '20px' }} />
                Got it, I'll do that!
              </button>
            </div>
          ) : (
            // Android/Desktop Install Button
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ fontSize: '14px', color: '#4b5563', margin: 0 }}>
                Get quick access to your budgets, shopping lists, and financial tasks.
              </p>
              <button
                type="button"
                data-install-btn
                onClick={handleInstall}
                disabled={installing}
                style={{
                  ...buttonStyle,
                  opacity: installing ? 0.5 : 1,
                  cursor: installing ? 'not-allowed' : 'pointer',
                }}
              >
                {installing ? (
                  <span style={{ 
                    width: '20px', 
                    height: '20px', 
                    border: '2px solid #ffffff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : (
                  <Download style={{ width: '20px', height: '20px' }} />
                )}
                {installing ? 'Installing...' : 'Install App'}
              </button>
              <p style={{ fontSize: '12px', textAlign: 'center', color: '#9ca3af', margin: 0 }}>
                No app store needed • Works offline
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
